import { useEffect, useState, useCallback } from "react";
import api, { fmtDate, formatApiError } from "@/lib/apiClient";
import { logoSrc } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Trash2, Paperclip, FileText, Plus } from "lucide-react";
import { toast } from "sonner";

export const DOC_TYPES = ["Safe Pass", "Public liability insurance", "Employers liability insurance", "Manual handling cert", "Electrical qualification", "MEWP", "Abrasive Wheel", "Work at Heights", "Confined Space", "Certification & Verification", "CSCS Mobile Tower Access", "Health & Safety Rep", "First Aid", "Driving licence", "Training certificate", "Medical certificate", "Employment contract", "Other"];

export function docStatus(expiry) {
  if (!expiry) return { label: "No expiry", cls: "bg-zinc-100 text-zinc-500" };
  const days = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  if (days < 0) return { label: "Expired", cls: "bg-rose-100 text-rose-700" };
  if (days <= 60) return { label: `Expires in ${days}d`, cls: "bg-amber-100 text-amber-800" };
  return { label: "Valid", cls: "bg-emerald-100 text-emerald-700" };
}

export function EmployeeDocs({ eid, onChange, readOnly }) {
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ doc_type: "Safe Pass", expiry: "", file_url: null, name: "" });
  const [uploading, setUploading] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({ expiry: "", file_url: null, name: "" });
  const [editUploading, setEditUploading] = useState(false);
  const load = useCallback(() => api.get(`/employees/${eid}/documents`).then((r) => setDocs(r.data)).catch(() => {}), [eid]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChange?.(); };
  const uploadTo = async (ev, setter, setBusy) => {
    const f = ev.target.files?.[0]; if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const res = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setter((p) => ({ ...p, file_url: res.data.file_url, name: f.name })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const add = async () => {
    try { await api.post(`/employees/${eid}/documents`, form); toast.success("Cert / document added"); setForm({ doc_type: "Safe Pass", expiry: "", file_url: null, name: "" }); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/employees/${eid}/documents/${id}`); refresh(); };
  const view = (d) => { const src = logoSrc(d.file_url || d.file); if (src) window.open(src, "_blank"); };
  const openEdit = (d) => { setEditDoc(d); setEditForm({ expiry: d.expiry || "", file_url: d.file_url || null, name: d.name || "" }); };
  const saveEdit = async () => {
    try { await api.put(`/employees/${eid}/documents/${editDoc.id}`, { expiry: editForm.expiry || null, file_url: editForm.file_url, name: editForm.name }); toast.success("Cert updated"); setEditDoc(null); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="space-y-1.5 mb-3">
        {docs.length === 0 && <div data-testid="emp-docs-empty" className="text-sm text-zinc-400">No certs or documents on file.</div>}
        {docs.map((d) => {
          const st = docStatus(d.expiry);
          return (
            <div key={d.id} data-testid={`emp-doc-${d.id}`} className="flex items-center justify-between border-b border-zinc-50 py-1.5 text-sm">
              <div className="min-w-0"><div className="font-medium truncate">{d.doc_type || d.name}</div><div className="text-xs text-zinc-500">{d.expiry ? `Expires ${fmtDate(d.expiry)}` : "No expiry"}</div></div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                {d.file_url && <button type="button" data-testid={`emp-doc-view-${d.id}`} onClick={() => view(d)} className="text-amber-600"><FileText className="h-4 w-4" /></button>}
                {!readOnly && <><button type="button" data-testid={`emp-doc-edit-${d.id}`} onClick={() => openEdit(d)} className="text-zinc-400 hover:text-slate-900"><Pencil className="h-4 w-4" /></button>
                <button type="button" data-testid={`emp-doc-del-${d.id}`} onClick={() => del(d.id)} className="text-rose-400"><Trash2 className="h-4 w-4" /></button></>}
              </div>
            </div>
          );
        })}
      </div>
      {!readOnly && <><div className="grid grid-cols-2 gap-2">
        <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
          <SelectTrigger data-testid="emp-doc-type"><SelectValue /></SelectTrigger>
          <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="emp-doc-expiry" type="date" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} />
      </div>
      <label className="mt-2 flex items-center gap-2 h-10 px-3 border border-dashed border-zinc-300 rounded-md cursor-pointer text-sm text-zinc-500 hover:border-amber-400">
        <Paperclip className="h-4 w-4" /><span className="truncate">{uploading ? "Uploading…" : (form.name || "Attach file (optional)")}</span>
        <input data-testid="emp-doc-file" type="file" accept="image/*,application/pdf" onChange={(e) => uploadTo(e, setForm, setUploading)} className="hidden" />
      </label>
      <Button type="button" data-testid="emp-doc-add" onClick={add} className="w-full bg-amber-500 text-black font-bold mt-2"><Plus className="h-4 w-4 mr-1" />Add cert / document</Button></>}

      <Dialog open={!!editDoc} onOpenChange={(o) => !o && setEditDoc(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit {editDoc?.doc_type || editDoc?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Expiry date</Label><Input data-testid="emp-doc-edit-expiry" type="date" value={editForm.expiry} onChange={(e) => setEditForm({ ...editForm, expiry: e.target.value })} className="mt-1" /></div>
            <label className="flex items-center gap-2 h-10 px-3 border border-dashed border-zinc-300 rounded-md cursor-pointer text-sm text-zinc-500 hover:border-amber-400">
              <Paperclip className="h-4 w-4" /><span className="truncate">{editUploading ? "Uploading…" : (editForm.name || "Replace file (optional)")}</span>
              <input data-testid="emp-doc-edit-file" type="file" accept="image/*,application/pdf" onChange={(e) => uploadTo(e, setEditForm, setEditUploading)} className="hidden" />
            </label>
            <Button type="button" data-testid="emp-doc-edit-save" onClick={saveEdit} className="w-full bg-slate-900 text-white">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
